/**
 * `/invoices` — customer-facing invoice list.
 *
 * Phase 2 stream D (Customer Portal migration from psm). Adapted from
 * `psm/portal/src/pages/InvoicesPage.tsx`, simplified for the new
 * AppShell + ui primitives stack:
 *   - Desktop-first table layout (the PSM page had a separate mobile
 *     card layout; that is deferred until the responsive shell lands).
 *   - Summary cards (Outstanding / Overdue / Paid this month) computed
 *     client-side from the same list response.
 *   - Search + status filter operate on the loaded set.
 *   - View / Download per-row buttons are RENDERED but `disabled` with a
 *     tooltip "Phase 3 で実装予定" so the surface area is preserved while
 *     making the not-yet-implemented state explicit (vs PSM's silent no-op).
 *     Phase 3 Stream E (Documents) will wire these to the GCS signed-URL
 *     download endpoint.
 *
 * Data source: `GET /api/portal/invoices` — BFF forwards to
 * `GET /api/internal/portal/invoices` (PSM authoritative store).
 *
 * Spec:
 *   - docs/specs/PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.2 stream D
 *   - docs/specs/PORTAL_INTERNAL_API_CONTRACT.md §2.5 I1
 */
import { useEffect, useMemo, useState } from 'react';
import { portalApi, PortalApiError } from '../lib/apiClient';
import {
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../components/ui';
import type { PortalInvoice } from '@customer-portal/shared';

interface InvoicesResponse {
  ok: boolean;
  data: {
    items: PortalInvoice[];
    total: number;
    limit: number;
    offset: number;
  };
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'success'; items: PortalInvoice[]; total: number }
  | { kind: 'error'; message: string };

const STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  overdue: 'Overdue',
  pending: 'Unpaid',
  unpaid: 'Unpaid',
  partial: 'Partially paid',
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status.toLowerCase()] ?? status;
}

function statusBadgeColors(status: string): { background: string; color: string } {
  const s = status.toLowerCase();
  if (s === 'paid') return { background: '#d1fae5', color: '#065f46' };
  if (s === 'overdue') return { background: '#fee2e2', color: '#991b1b' };
  if (s === 'pending' || s === 'unpaid' || s === 'partial')
    return { background: '#fef3c7', color: '#92400e' };
  return { background: '#f3f4f6', color: '#374151' };
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' });
}

function formatAmount(amount: number | null | undefined, currency: string): string {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'JPY',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency}`;
  }
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const colors = statusBadgeColors(status);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: colors.background,
        color: colors.color,
      }}
    >
      {statusLabel(status)}
    </span>
  );
}

const PHASE3_TOOLTIP = 'Coming in Phase 3';

/**
 * Disabled icon-button placeholder. Renders the same shape as the
 * eventual View / Download control but is non-interactive with a
 * tooltip explaining when it lights up. Keeps the layout stable so
 * Phase 3 Stream E can drop in the real handler without an unrelated
 * visual diff.
 */
function DisabledActionButton({ label, icon }: { label: string; icon: string }): JSX.Element {
  return (
    <button
      type="button"
      disabled
      aria-label={label}
      aria-disabled="true"
      title={PHASE3_TOOLTIP}
      data-not-yet-implemented="phase-3-stream-e"
      style={{
        width: 28,
        height: 28,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #e5e7eb',
        borderRadius: '0.25rem',
        background: '#f9fafb',
        color: '#9ca3af',
        cursor: 'not-allowed',
        fontSize: '0.75rem',
        marginLeft: '0.25rem',
      }}
    >
      <span aria-hidden="true">{icon}</span>
    </button>
  );
}

export function InvoicesPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: 'loading' });
    portalApi<InvoicesResponse>('/api/portal/invoices', { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        const items = res.data?.items ?? [];
        setState({ kind: 'success', items, total: res.data?.total ?? items.length });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalApiError) {
          setState({ kind: 'error', message: err.code });
        } else {
          setState({ kind: 'error', message: 'UNKNOWN' });
        }
      });
    return () => ctrl.abort();
  }, [reloadTick]);

  const items = useMemo<PortalInvoice[]>(
    () => (state.kind === 'success' ? state.items : []),
    [state],
  );

  const filteredItems = useMemo(() => {
    let result = items;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((inv) => inv.invoice_no.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      result = result.filter((inv) => inv.status.toLowerCase() === statusFilter);
    }
    return result;
  }, [items, search, statusFilter]);

  const currency = items[0]?.currency_code || 'JPY';

  const totalOutstanding = useMemo(
    () =>
      items
        .filter((inv) => inv.status.toLowerCase() !== 'paid')
        .reduce((sum, inv) => sum + (inv.invoice_total_amount ?? 0), 0),
    [items],
  );
  const overdueAmount = useMemo(
    () =>
      items
        .filter((inv) => inv.status.toLowerCase() === 'overdue')
        .reduce((sum, inv) => sum + (inv.invoice_total_amount ?? 0), 0),
    [items],
  );
  const paidThisMonth = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    return items
      .filter((inv) => {
        if (inv.status.toLowerCase() !== 'paid' || !inv.issued_at) return false;
        const d = new Date(inv.issued_at);
        return d.getFullYear() === year && d.getMonth() === month;
      })
      .reduce((sum, inv) => sum + (inv.invoice_total_amount ?? 0), 0);
  }, [items]);
  const overdueCount = useMemo(
    () => items.filter((inv) => inv.status.toLowerCase() === 'overdue').length,
    [items],
  );

  return (
    <section data-testid="invoices-page" style={{ display: 'grid', gap: '1.5rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Invoices</h1>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
          View and manage your invoices
        </p>
      </header>

      {state.kind === 'loading' ? <Spinner block label="Loading invoices" /> : null}

      {state.kind === 'error' ? (
        <ErrorState
          title="Couldn't load invoices"
          description={`Error code: ${state.message}`}
          onRetry={() => setReloadTick((n) => n + 1)}
          retryLabel="Retry"
        />
      ) : null}

      {state.kind === 'success' ? (
        <>
          {/* Summary cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '1rem',
            }}
            data-testid="invoices-summary"
          >
            <Card aria-label="Total outstanding">
              <CardBody>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                  Total Outstanding
                </p>
                <p
                  style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700 }}
                  data-testid="invoices-summary-outstanding"
                >
                  {formatAmount(totalOutstanding, currency)}
                </p>
              </CardBody>
            </Card>
            <Card aria-label="Overdue amount">
              <CardBody>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>Overdue Amount</p>
                <p
                  style={{
                    margin: '0.25rem 0 0',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: overdueAmount > 0 ? '#dc2626' : undefined,
                  }}
                  data-testid="invoices-summary-overdue"
                >
                  {formatAmount(overdueAmount, currency)}
                </p>
              </CardBody>
            </Card>
            <Card aria-label="Paid this month">
              <CardBody>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>Paid This Month</p>
                <p
                  style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700 }}
                  data-testid="invoices-summary-paid-this-month"
                >
                  {formatAmount(paidThisMonth, currency)}
                </p>
              </CardBody>
            </Card>
          </div>

          {/* Filters */}
          <Card aria-label="Invoice filters">
            <CardBody>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: '0.75rem',
                    color: '#374151',
                    flex: '1 1 14rem',
                    minWidth: '12rem',
                  }}
                >
                  <span style={{ marginBottom: '0.25rem' }}>Search invoice no</span>
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="INV-..."
                    style={{
                      padding: '0.375rem 0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.25rem',
                      fontSize: '0.875rem',
                    }}
                    data-testid="invoices-search-input"
                  />
                </label>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: '0.75rem',
                    color: '#374151',
                  }}
                >
                  <span style={{ marginBottom: '0.25rem' }}>Status</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      padding: '0.375rem 0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.25rem',
                      fontSize: '0.875rem',
                    }}
                    data-testid="invoices-status-filter"
                  >
                    <option value="all">All statuses</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="partial">Partial</option>
                  </select>
                </label>
              </div>
            </CardBody>
          </Card>

          <p
            style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}
            data-testid="invoices-result-count"
            aria-live="polite"
          >
            {filteredItems.length} invoice{filteredItems.length !== 1 ? 's' : ''} found
          </p>

          {filteredItems.length === 0 ? (
            <EmptyState
              title="No invoices found"
              description={
                items.length === 0 ? 'You have no invoices yet' : 'No invoices match your filters'
              }
            />
          ) : (
            <Card aria-label="Invoices table">
              <Table aria-label="Invoices">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Invoice No</TableHeaderCell>
                    <TableHeaderCell>Invoice Date</TableHeaderCell>
                    <TableHeaderCell>Due Date</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: 'right' }}>Amount</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: 'right' }}>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.map((inv) => {
                    const isOverdue = inv.status.toLowerCase() === 'overdue';
                    return (
                      <TableRow key={inv.id}>
                        <TableCell
                          style={{
                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                            fontWeight: 500,
                          }}
                        >
                          {inv.invoice_no}
                        </TableCell>
                        <TableCell>{formatDate(inv.issued_at)}</TableCell>
                        <TableCell
                          style={isOverdue ? { color: '#dc2626', fontWeight: 600 } : undefined}
                        >
                          {formatDate(inv.due_date)}
                        </TableCell>
                        <TableCell
                          style={{
                            textAlign: 'right',
                            fontWeight: 600,
                            color: isOverdue ? '#dc2626' : undefined,
                          }}
                        >
                          {formatAmount(inv.invoice_total_amount, inv.currency_code)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={inv.status} />
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          <DisabledActionButton label="View invoice (Phase 3)" icon="👁" />
                          <DisabledActionButton label="Download invoice (Phase 3)" icon="↓" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Overdue payment notice */}
          {overdueCount > 0 ? (
            <ErrorState
              title="Overdue Payment Notice"
              description={`You have ${overdueCount} overdue invoice${overdueCount !== 1 ? 's' : ''} totalling ${formatAmount(overdueAmount, currency)}. Please arrange payment as soon as possible.`}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
